/**
 * The attendance archive as a table — the wide-screen half of both archives.
 *
 * There are two places the archive is read: the admin student page, and the teacher's own
 * `/staff-app/history`. They ask different questions of it, and they were built to
 * different shapes because of that — the admin an eight-column table, the teacher a card
 * per session date.
 *
 * That was the right split by *question* and the wrong split by *screen*. An admin on a
 * phone got the eight columns and a horizontal scroll; a teacher at a desk got a column of
 * phone cards with two thirds of the window empty. So the split moved: a table on a wide
 * screen and cards on a narrow one, in **both** places, from this one component and
 * `AttendanceRecordCard` beside it. Neither surface owns a layout the other cannot have.
 *
 * What still differs between them is what they are *allowed* to ask for — the teacher's
 * rows come back from `staff_attendance_history`, scoped in the database to their own
 * grant — and how the rows are grouped. Grouping is this component's `sections`, so the
 * admin can group by school and grade band while the teacher groups by day, and the row
 * itself is rendered identically either way.
 */

import { Fragment, type ReactNode } from 'react';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import { AttendanceStatusBadge } from './AttendanceStatusBadge';
import {
  HISTORY_STATUS_LABEL,
  attendanceClassLabel,
  checkedInByLabel,
  type AttendanceHistoryRow,
} from '../../utils/attendanceHistory';
import { formatDateDisplay, formatTimeInBusinessTZ } from '../../utils/date';

/**
 * A run of rows under one heading.
 *
 * A `null` label renders the rows with no heading at all, which is the flat table — camps
 * and enrichment have nothing to group by, and saying so with an empty heading row would
 * be worse than saying nothing.
 */
export interface AttendanceHistorySection {
  key: string;
  label: string | null;
  /** Beside the label: a row count for the admin, a present/absent tally for the teacher. */
  meta?: ReactNode;
  rows: readonly AttendanceHistoryRow[];
}

interface Props {
  sections: readonly AttendanceHistorySection[];
  /**
   * Whether each row carries its own date.
   *
   * False where the section heading is already the date — repeating it down every row of a
   * day card's table is a column that says the same word twelve times.
   */
  showDate?: boolean;
  expandedId: string | null;
  onToggleExpanded: (id: string | null) => void;
}

function clock(iso: string | null): string {
  return iso ? formatTimeInBusinessTZ(iso, { hour12: true }) : '—';
}

/**
 * Who collected the child, with the flag when they were not on the authorized list.
 *
 * The warning is the single most consequential thing on this screen, so it is a glyph *and*
 * a colour *and* screen-reader text — never colour alone.
 */
function CollectedBy({ row }: { row: AttendanceHistoryRow }) {
  if (row.status !== 'picked_up') return <span className="text-gray-300">—</span>;
  const name = row.pickedUpByName ?? 'someone unrecorded';
  if (row.pickupIsAuthorized === false) {
    return (
      <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
        {name}
        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
        <span className="sr-only">not on the authorized pickup list</span>
      </span>
    );
  }
  return <span>{name}</span>;
}

/** The facts a table cell has no room for, shown on demand rather than in another column. */
function RowDetail({ row }: { row: AttendanceHistoryRow }) {
  const facts: string[] = [];
  const checkedInBy = checkedInByLabel(row);
  if (checkedInBy) facts.push(`Checked in by ${checkedInBy}`);
  if (row.pickupRecordedByName) facts.push(`Released by ${row.pickupRecordedByName}`);
  if (row.absenceReason) facts.push(`Absence reason: ${row.absenceReason}`);
  if (row.programName) facts.push(`Program: ${row.programName}`);
  if (row.studentGrade) facts.push(`Grade: ${row.studentGrade}`);

  return (
    <div className="px-4 py-2.5 bg-gray-50 text-xs text-gray-600 space-y-0.5">
      {facts.length === 0 ? (
        <p className="text-gray-400">Nothing further was recorded for this session.</p>
      ) : (
        facts.map(fact => <p key={fact}>{fact}</p>)
      )}
    </div>
  );
}

/**
 * One record. A component rather than inline markup because the body renders it two ways —
 * flat, or inside a section — and a duplicated row is where the two would drift apart.
 */
function HistoryTableRow({
  row,
  columns,
  showDate,
  expanded,
  onToggleExpanded,
}: {
  row: AttendanceHistoryRow;
  columns: number;
  showDate: boolean;
  expanded: boolean;
  onToggleExpanded: (id: string | null) => void;
}) {
  return (
    <Fragment>
      <tr className={expanded ? 'bg-blue-50/50' : 'hover:bg-gray-50'}>
        {showDate && (
          <td className="px-4 py-2.5 text-sm text-gray-600 whitespace-nowrap">
            {formatDateDisplay(row.sessionDate)}
          </td>
        )}
        <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{row.studentSortName}</td>
        <td className="px-4 py-2.5 text-sm text-gray-600">
          {attendanceClassLabel(row.virtualClassKey, row.programName)}
        </td>
        <td className="px-4 py-2.5">
          <AttendanceStatusBadge status={row.status} labels={HISTORY_STATUS_LABEL} />
        </td>
        <td className="px-4 py-2.5 text-sm text-gray-500 whitespace-nowrap">{clock(row.checkedInAt)}</td>
        <td className="px-4 py-2.5 text-sm text-gray-500 whitespace-nowrap">{clock(row.pickedUpAt)}</td>
        <td className="px-4 py-2.5 text-sm text-gray-600"><CollectedBy row={row} /></td>
        <td className="px-2 py-2.5">
          <button
            type="button"
            onClick={() => onToggleExpanded(expanded ? null : row.id)}
            aria-expanded={expanded}
            aria-label={`Details for ${row.studentSortName} on ${formatDateDisplay(row.sessionDate)}`}
            className="p-1 text-gray-400 hover:text-gray-700 rounded"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={columns} className="p-0"><RowDetail row={row} /></td>
        </tr>
      )}
    </Fragment>
  );
}

export function AttendanceHistoryTable({
  sections,
  showDate = true,
  expandedId,
  onToggleExpanded,
}: Props) {
  const headings = [
    ...(showDate ? ['Date'] : []),
    'Student',
    'Class',
    'Status',
    'In',
    'Out',
    'Collected by',
    '',
  ];

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {headings.map((heading, i) => (
              <th
                key={heading || `col-${i}`}
                scope="col"
                className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sections.map(section => (
            <Fragment key={section.key}>
              {section.label !== null && (
                <tr className="bg-gray-100/70">
                  <th
                    scope="colgroup"
                    colSpan={headings.length}
                    className="px-4 py-2 text-left text-xs font-semibold text-gray-700"
                  >
                    {section.label}
                    {section.meta !== undefined && (
                      <span className="ml-2 font-normal text-gray-400">{section.meta}</span>
                    )}
                  </th>
                </tr>
              )}
              {section.rows.map(row => (
                <HistoryTableRow
                  key={row.id}
                  row={row}
                  columns={headings.length}
                  showDate={showDate}
                  expanded={expandedId === row.id}
                  onToggleExpanded={onToggleExpanded}
                />
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
